import {
	AnyHighlightComponentRequest,
	HighlightsContainerRequest,
	HighlightsContainerResponse,
	HighlightsSectionRequest,
} from '@front-app/modules/group/highlights/model';
import { isNonEmptySection } from '@front-app/modules/group/highlights/util/compare';
import { STATUS } from '@front-app/constants/state';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import { fetchGroupCustomPage, upsertGroupCustomPage, upsertGroupHighlights, fetchGroupHighlights } from '@front-app/services/GroupsApi/GroupsAdmin';
import {
	computed,
	Ref,
	ref,
	watchEffect,
} from '@vue/composition-api';
import { STARTABLE_STATUSES } from '../util/cache';

type HighlightsFetcher = (id: string) => Promise<HighlightsContainerResponse[] | null>;
type HighlightsUpdater = (id: string, request: HighlightsContainerRequest[]) => Promise<HighlightsContainerResponse[]>;
type AfterFetch = (v: HighlightsContainerResponse[]) => void;

interface HighlightsAdminStore {
	highlights: Ref<HighlightsContainerResponse[]>;
	status: Ref<STATUS>;
	fetch(id: string, force?: boolean): Promise<void>;
	update(id: string, request: HighlightsContainerRequest[]): Promise<void>;
}

interface HighlightsCache {
	status: Ref<STATUS>;
	highlights: Ref<HighlightsContainerResponse[]>;
}

export const getHighlightsAdminStore = (
	groupId: Ref<string>,
	fetcher: HighlightsFetcher,
	updater: HighlightsUpdater,
	cache: Record<string, HighlightsCache> = {},
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	afterFetch: AfterFetch = () => {},
): HighlightsAdminStore => {
	const innerCache = cache;
	const fetch = async (id: string, force = false): Promise<void> => {
		innerCache[id] = innerCache[id] || newCache();
		if (force || STARTABLE_STATUSES.includes(innerCache[id].status.value)) {
			innerCache[id].status.value = STATUS.LOADING;
			const result = await fetcher(id);
			if (result && result.length > 0) {
				innerCache[id].highlights.value = result;
				afterFetch(result);
			}
			innerCache[id].status.value = STATUS.SUCCESS;
		}
	};
	const update = async (id: string, request: HighlightsContainerRequest[]): Promise<void> => {
		const payload = removeNewIds(request);

		payload.forEach((container) => {
			container.sections = container.sections.filter(isNonEmptySection);
		})

		innerCache[id] = innerCache[id] || newCache();
		const { track } = useAsyncStatus(innerCache[id].status);
		innerCache[id].highlights.value = await track(updater(id, removeNewIds(payload)));
	};
	const highlights: Ref<HighlightsContainerResponse[]> = computed(() => {
		innerCache[groupId.value] = innerCache[groupId.value] || newCache();
		return innerCache[groupId.value].highlights.value;
	});
	const status: Ref<STATUS> = computed(() => {
		innerCache[groupId.value] = innerCache[groupId.value] || newCache();
		return innerCache[groupId.value].status.value;
	});
	watchEffect(() => {
		fetch(groupId.value);
	});
	return {
		highlights,
		status,
		fetch,
		update,
	};
};

const groupCustomPageCache: Record<string, HighlightsCache> = {};
export const useGroupCustomPage = (
	groupId: Ref<string>,
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	afterFetch: AfterFetch = () => {},
): HighlightsAdminStore => getHighlightsAdminStore(
	groupId,
	fetchGroupCustomPage,
	upsertGroupCustomPage,
	groupCustomPageCache,
	afterFetch,
);

const groupCustomHighlightsCache: Record<string, HighlightsCache> = {};
export const useGroupsCustomHighlights = (
	groupId: Ref<string>,
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	afterFetch: AfterFetch = () => {},
): HighlightsAdminStore => getHighlightsAdminStore(
	groupId,
	fetchGroupHighlights,
	upsertGroupHighlights,
	groupCustomHighlightsCache,
	afterFetch,
);

function removeNewIds(containers: HighlightsContainerRequest[]): HighlightsContainerRequest[] {
	return containers.map((container) => {
		return {
			...container,
			sections: container.sections.map(removeSectionNewIds),
		}
	});
}
function removeSectionNewIds(req: HighlightsSectionRequest): HighlightsSectionRequest {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { newId, elements, ...values } = req;
	return {
		...values,
		elements: elements.map(removeElementNewIds),
	};
}
function removeElementNewIds(req: AnyHighlightComponentRequest): AnyHighlightComponentRequest {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { newId, ...values } = req;
	return values;
}

function newCache(): HighlightsCache {
	const highlights: Ref<HighlightsContainerResponse[]> = ref([{
		createdDateTime: new Date().toISOString(),
		lastModifiedDateTime: new Date().toISOString(),
		sections: [],
		meta: {
			name: '',
			width: 100,
		}
	}]);
	return {
		status: ref(STATUS.UNSTARTED),
		highlights,
	};
}
